/**
 * Utility for encoding/detecting Amazon customer IDs.
 *
 * Obfuscated format: Starts with 'A' followed by alphanumeric chars (e.g., "A23ZP02F085DFQ")
 * Unencrypted format: Numeric only (e.g., "123456789")
 */

const OBFUSCATED_PATTERN = /^A[A-Z0-9]{10,}$/;
const NUMERIC_PATTERN = /^\d+$/;
const BASE36_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ZERO = BigInt(0);
const BASE = BigInt(36);

export class CustomerIdEncoder {
  /**
   * Check if the customer ID is already in obfuscated format
   */
  static isObfuscated(customerId: string): boolean {
    return OBFUSCATED_PATTERN.test(customerId.toUpperCase());
  }

  /**
   * Check if the customer ID is in numeric (unencrypted) format
   */
  static isNumeric(customerId: string): boolean {
    return NUMERIC_PATTERN.test(customerId);
  }

  /**
   * Encode a numeric customer ID to obfuscated format.
   * Uses base36 encoding with 'A' prefix (Amazon's standard format).
   */
  static encode(numericId: string): string {
    if (!this.isNumeric(numericId)) {
      return numericId; // Already encoded or invalid
    }

    let num = BigInt(numericId);
    if (num === ZERO) {
      return "A" + "0".repeat(13);
    }

    let result = "";
    while (num > ZERO) {
      result = BASE36_CHARS[Number(num % BASE)] + result;
      num = num / BASE;
    }

    // Pad to ensure consistent length (min 13 chars after 'A')
    return "A" + result.padStart(13, "0");
  }

  /**
   * Normalize customer ID - encode if numeric, return as-is if already obfuscated
   */
  static normalize(customerId: string): string {
    const trimmed = customerId.trim();
    if (this.isNumeric(trimmed)) {
      return this.encode(trimmed);
    }
    return trimmed;
  }

  /**
   * Get display info about the customer ID format
   */
  static getFormatInfo(customerId: string): { isObfuscated: boolean; isNumeric: boolean; normalized: string } {
    const trimmed = customerId.trim();
    return {
      isObfuscated: this.isObfuscated(trimmed),
      isNumeric: this.isNumeric(trimmed),
      normalized: this.normalize(trimmed),
    };
  }
}
